//@flow
import { Box, Button, Card, Divider, Paper, Toolbar, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from "react-i18next";
import CloudDownloadIcon from "../../../../../assets/images/icons/buttons/cloudDownloadIcon";
import TrashIcon from "../../../../../assets/images/icons/buttons/trashIcon";
import UploadImageIcon from "../../../../../assets/images/icons/general/uploadImageIcon";
import ExhibitionUpdateStatus from "../../../../../components/app/alerts/exhibitionUpdateStatus/exhibitionUpdateStatus";
import FileUpload from "../../../../../components/form/fileUpload/fileUpload";
import { AuthenticatedLink } from "../../../../../components/general/authenticatedLink/authenticatedLink";
import ContentLoader from "../../../../../components/general/contentLoader/contentLoader";
import CustomTranslation from "../../../../../components/general/customTranslation/customTranslation";
import SkeletonLoaderWrapper from "../../../../../components/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { API, FileTypes } from "../../../../../helpers/constants";
import { handleAxiosErrors } from "../../../../../helpers/errorHandling";
import { useExhibitionFileUpload } from "../../../../../hooks/exhibitionFIles/useExhibitionFileUpload";
import { useExhibition } from "../../../../../hooks/exhibitions/useExhibition";
import useUserData from "../../../../../hooks/user/useUserData";
import { EXHIBITION } from "../../../../../locales/components/namespaces";

const ExhibitorKit = (props) => {
	const { t } = useTranslation(EXHIBITION)
	const { exhibitor } = props
	const { token } = useUserData()
	const theme = useTheme()
	const { exhibition, files, id } = useExhibition({ language: 'any' })
	const [loading, setLoading] = useState(false)
	const [loadingDelete, setLoadingDelete] = useState(false)
	const [image, setImage] = useState("");
	const {
		getFiles,
	} = useExhibitionFileUpload({
		exhibitionId: id,
		typeId: FileTypes.ExhibitorKit
	})
	
	const getAdLogo = () => {
		setLoading(true)
		axios({
			url: `${API}/en/exhibitor/get-logo/${id}?ExhibitorId=${exhibitor?.id}`,
			method: 'get',
			responseType: "arraybuffer",
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
		}).then((response) => {
			const mime = (response.headers['content-type'] || 'image/png').split(';')[0];
			const blob = new Blob([response.data], { type: mime });
			const url = URL.createObjectURL(blob);
			// Create default browser Image object to load programmatically an image and inspect properties before displaying it
			const img = new Image();
			// Set action on image load. This check is required because backend returns an 1*1 image if no image found.
			// We need to exclude setting the 1*1 image from state, to display image placeholder instead when no image found.
			img.onload = () => {
				URL.revokeObjectURL(url);
				if (!(img.width === 1 && img.height === 1)) {
					const base64 = btoa(
						new Uint8Array(response.data).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							""
						)
					)
					setImage(base64)
				}
			}
			// Set action on image load error
			img.onerror = () => {
				URL.revokeObjectURL(url)
			}
			// Set image src. If not set Image object will not load anything until you give it a source.
			// When you set in onload event gets called, and you can check for image dimensions.
			img.src = url
			setLoading(false)
		}).catch(() => {
			setLoading(false)
		})
	}
	
	useEffect(() => {
		getAdLogo()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	
	const uploadFile = (file) => {
		axios({
			url: `${API}/en/exhibitor/upload-logo/${id}`,
			method: 'post',
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
			data: {
				file: file,
				ExhibitorId: exhibitor?.id
			}
		}).then(() => {
			getAdLogo()
			enqueueSnackbar(t('Advertisement logo uploaded successfully!'), {
				variant: 'success',
			})
		}).catch((error) => {
			const apiMessage = error?.response?.data?.message
			enqueueSnackbar(handleAxiosErrors(error, `${t('Failed to upload advertisement logo!')} ${t(apiMessage)}`), {
				variant: 'error',
			})
		})
	}
	
	const deleteAdLogo = () => {
		setLoadingDelete(true)
		axios({
			url: `${API}/en/exhibitor/delete-logo/${id}`,
			method: 'post',
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
			data: {
				ExhibitorId: exhibitor?.id
			}
		}).then(() => {
			setLoadingDelete(false)
			setImage('')
			enqueueSnackbar(t('Advertisement logo deleted successfully!'), {
				variant: 'success',
			})
		}).catch((error) => {
			setLoadingDelete(false)
			enqueueSnackbar(handleAxiosErrors(error, t('Failed to delete advertisement logo!')), {
				variant: 'error',
			})
		})
	}
	
	const kitFile = useMemo(() => {
		const kit = getFiles(files.filter(f => f.type === FileTypes.ExhibitorKit), '')
		return kit && kit.length > 0 ? kit[0] : null
	}, [files, getFiles])
	
	return (
		<div>
			{exhibition ? (
				<React.Fragment>
					<Grid container spacing={4}>
						<Grid size={{ xs: 12 }}>
							<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
								<CustomTranslation
									code={'marketing_kit'}
									translations={exhibition?.translations}
								>
									<Typography component={"div"} variant={"body1"}>
										<Trans
											i18nKey={'exhibitor_kit_and_ad_logos'}
											ns={EXHIBITION}
										>
											Here you can upload your company's logo
											in <strong>JPG</strong> or <strong>PNG</strong> format at resolution 2250 x 1010, for use in advertisements and in the <strong>exhibitor
											list</strong>.<br/>
											Additionally you can download the <strong>Marketing Kit</strong>,
											useful <strong>promotional material</strong> to better promote your
											participation in the exhibition.
										</Trans>
									</Typography>
								</CustomTranslation>
							</Paper>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
							<Paper
								sx={{
									px: 2,
									p: 4,
									border: '1px solid',
									borderColor: theme.palette.primary[200],
									borderRadius: '12px'
								}}
								variant={"outlined"}
							>
								<Toolbar disableGutters variant={"dense"}>
									<Typography variant="body" color={theme.palette.primary[800]} fontWeight={700}>
										{t('Advertisement Logo')}
									</Typography>
								
								</Toolbar>
								<Divider sx={{ mb: 4, border: '1px solid', borderColor: theme.palette.primary[800] }}/>
								<FileUpload
									accept={{
										'image/jpeg': [],
										'image/png': [],
									}}
									multiple={false}
									onFilesUpload={(files) => {
										if (files && files.length > 0) uploadFile(files[0])
									}}
									showImageIcon={true}
								/>
								<Card
									sx={{
										border: '1px solid',
										borderColor: theme.palette.primary[300],
										mt: 4,
										mb: 4,
										borderRadius: '12px',
										boxShadow: 'none',
										p: 4,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									}}>
									<SkeletonLoaderWrapper loading={loading}>
										{image ? (
											<img
												src={`data:image/jpeg;base64,${image}`}
												alt="ad logo"
												style={{ maxWidth: '100%', maxHeight: '600px' }}
											/>
										) : (
											<UploadImageIcon/>
										)}
									</SkeletonLoaderWrapper>
								</Card>
								<Box display="flex" justifyContent="flex-end">
									<Button
										loading={loadingDelete}
										color={"error"}
										loadingPosition={"start"}
										variant={"outlined"}
										size={"small"}
										startIcon={<TrashIcon/>}
										onClick={deleteAdLogo}
										sx={{
											marginLeft: 'auto'
										}}
									>
										{t('Delete')}
									</Button>
								</Box>
							</Paper>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
							<Box>
								<Grid container spacing={4}>
									<Grid size={12}>
										<Paper
											sx={{
												px: 2,
												p: 4,
												border: '1px solid',
												borderColor: theme.palette.primary[200],
												borderRadius: '12px'
											}}
											variant={"outlined"}>
											<Box>
												<Toolbar disableGutters variant={"dense"}>
													<Typography variant="body" color={theme.palette.primary[800]} fontWeight={700}>
														{t('Marketing Kit')}
													</Typography>
												</Toolbar>
												<Divider
													sx={{ mb: 4, border: '1px solid', borderColor: theme.palette.primary[800] }}/>
											</Box>
											{kitFile ? (
												<AuthenticatedLink
													filename={kitFile.name}
													url={kitFile.url}
												>
													<Button
														variant={"contained"}
														color={"highlight"}
														sx={{ width: '100%' }}
														size={"small"}
														startIcon={<CloudDownloadIcon stroke={"#fff"}/>}
													>
														{t('Download an exhibition marketing kit')}
													</Button>
												</AuthenticatedLink>
											) : (
												<Typography variant="body1">
													{t('Marketing Kit has not been uploaded yet from exhibition organizer.')}
												</Typography>
											)}
										</Paper>
									</Grid>
									<Grid size={12}>
										<Paper
											sx={{
												px: 2,
												p: 4,
												border: '1px solid',
												borderColor: theme.palette.primary[200],
												borderRadius: '12px'
											}}
											variant={"outlined"}>
											<Box>
												<Toolbar disableGutters variant={"dense"}>
													<Typography variant="body" color={theme.palette.primary[800]} fontWeight={700}>
														{t('Advertisement Logo With Kiosk Information')}
													</Typography>
												</Toolbar>
												<Divider
													sx={{ mb: 4, border: '1px solid', borderColor: theme.palette.primary[800] }}/>
											</Box>
											{image ? (
												<AuthenticatedLink
													filename={t('Advertisement Logo')}
													url={`${API}/en/exhibition/getmarketingkitlogo/${id}?Download=1&ExhibitorId=${exhibitor.id}`}
												>
													<Button
														variant={"contained"}
														color={"highlight"}
														sx={{ width: '100%' }}
														size={"small"}
														startIcon={<CloudDownloadIcon stroke={"#fff"}/>}
													>
														{t('Download Logo')}
													</Button>
												</AuthenticatedLink>
											) : (
												<Typography variant="body1">
													{t("You haven't uploaded yet your advertisement logo.")}
												</Typography>
											)}
										</Paper>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					</Grid>
				</React.Fragment>
			) : (
				<ContentLoader
					loading={loading}
				/>
			)}
			<ExhibitionUpdateStatus/>
		</div>
	)
}

export default ExhibitorKit;
